import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, inject, input, output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatRadioModule} from '@angular/material/radio';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatIconModule} from '@angular/material/icon';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {
  RECURRENCE_HORIZON_DAYS,
  RECURRENCE_INTERVAL_UNIT,
  RECURRENCE_MAX_OCCURRENCES,
  RECURRENCE_NTH_WEEKS,
  RECURRENCE_WEEKDAYS,
  RecurrenceFrequency,
  RecurrenceNthWeek,
  RecurrenceRequest,
  RecurrenceWeekday,
  maxIntervalFor,
  pickerToWallClock,
} from '@xmedical/core';
import dayjs from 'dayjs/esm';

type Dayjs = dayjs.Dayjs;

/**
 * Whole numbers only.
 *
 * Every recurrence number maps to an integer field on the server. A `type="number"` input
 * happily yields 1.5, and min/max both pass it, so without this the form stays valid and the
 * request is rejected server-side instead of being explained here. The `step` attribute is no
 * substitute: this block is hosted on a div rather than a form, so native constraint
 * validation never runs, and Angular reads validity from validators rather than attributes.
 *
 * An empty value is `required`'s business, not this validator's — reporting both would put
 * two errors on one empty field.
 */
function wholeNumber(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return Number.isInteger(Number(value)) ? null : {wholeNumber: true};
}

/**
 * The "repeat this appointment" block on the new-appointment dialog.
 *
 * Emits a RecurrenceRequest (or null when the toggle is off) rather than writing to a
 * parent form — the parent only has to drop the value onto the appointment it builds.
 *
 * The server rejects several field combinations outright (both monthly branches set, both
 * or neither end condition). Rather than validate after the fact, the form makes them
 * unrepresentable: the monthly pattern and the end condition are each a radio group with
 * exactly one live branch, and only the live branch's control is enabled. What's left to
 * validate is presence and numeric range, which is done with ordinary validators.
 *
 * Blocked time only — the parent decides whether to show this at all, via
 * `canRepeatAppointmentType`. See the note in recurrence.ts for why the gate is the type.
 */
@Component({
  selector: 'app-appointment-recurrence-form',
  templateUrl: './appointment-recurrence-form.component.html',
  styleUrls: ['./appointment-recurrence-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDatepickerModule,
  ],
})
export class AppointmentRecurrenceFormComponent implements OnInit, OnDestroy {
  /**
   * The appointment's start date. Seeds the weekly day, the monthly pattern and the
   * default end date, and bounds the end date to the server's horizon.
   *
   * Live — the parent binds its date control, so this changes while the dialog is open.
   * Seeded defaults follow it until the user edits them; see `reseedFromStart`.
   */
  startDate = input<Date | null>(null);

  /** Fires on every change: the rule to send, or null when repeating is switched off. */
  recurrenceChange = output<RecurrenceRequest | null>();
  /** Fires when the form's own validity changes, so the parent can gate its save button. */
  validityChange = output<boolean>();

  form: FormGroup;

  readonly weekdays = RECURRENCE_WEEKDAYS;
  readonly nthWeeks = RECURRENCE_NTH_WEEKS;
  readonly maxOccurrences = RECURRENCE_MAX_OCCURRENCES;
  readonly horizonDays = RECURRENCE_HORIZON_DAYS;

  private formBuilder = inject(FormBuilder);

  private formSubscription: Subscription;
  private frequencySubscription: Subscription;
  private monthlyModeSubscription: Subscription;
  private endsModeSubscription: Subscription;
  private enabledSubscription: Subscription;

  constructor() {
    // The start date can change while the dialog is open (the user picks a different slot
    // before switching the repeat on). Without this the seeded weekday would still describe
    // the original date, and a weekly repeat would silently land on the wrong day.
    effect(() => {
      const start = this.startDate();
      if (this.form && start) {
        this.reseedFromStart(start);
      }
    });
  }

  ngOnInit(): void {
    const start = dayjs(this.startDate() ?? new Date());

    this.form = this.formBuilder.group({
      enabled: [false],
      frequency: ['Weekly' as RecurrenceFrequency],
      // Max is not declared here: it depends on the frequency, which changes while the form is
      // open. See `intervalWithinFrequencyLimit`.
      interval: [1, [Validators.required, Validators.min(1), wholeNumber]],
      // Seeded with the appointment's own weekday: "repeat weekly" for a Monday booking
      // means Mondays until told otherwise.
      weeklyDays: [[this.weekdays[start.day()]] as RecurrenceWeekday[]],
      monthlyMode: ['ByDayOfMonth' as 'ByDayOfMonth' | 'ByNthWeekday'],
      monthlyDayOfMonth: [start.date(), [Validators.required, Validators.min(1), Validators.max(31), wholeNumber]],
      monthlyNth: ['First' as RecurrenceNthWeek, Validators.required],
      monthlyWeekday: [this.weekdays[start.day()] as RecurrenceWeekday, Validators.required],
      endsMode: ['Count' as 'Count' | 'Date'],
      // Required on both branches: min/max pass an empty value, so without this a cleared
      // field stays "valid" and emits a rule with neither end condition — which the server
      // rejects, having asked for exactly one.
      endsAfterCount: [10, [Validators.required, Validators.min(1), Validators.max(RECURRENCE_MAX_OCCURRENCES), wholeNumber]],
      // Default a month out — inside the 365-day horizon for any frequency.
      endsOnDate: [start.add(1, 'month').toDate(), Validators.required],
    }, {validators: [
      control => this.intervalWithinFrequencyLimit(control),
      control => this.endDateWithinHorizon(control),
      control => this.countWithinHorizon(control),
      control => this.dateWithinOccurrenceCap(control),
    ]});

    this.syncEnabledControls();
    this.formSubscription = this.form.valueChanges.subscribe(() => this.emit());

    // Each of these changes which controls are live, which changes what "valid" means.
    this.frequencySubscription = this.form.get('frequency').valueChanges
      .subscribe(() => this.syncEnabledControls());
    this.monthlyModeSubscription = this.form.get('monthlyMode').valueChanges
      .subscribe(() => this.syncEnabledControls());
    this.endsModeSubscription = this.form.get('endsMode').valueChanges
      .subscribe(() => this.syncEnabledControls());
    this.enabledSubscription = this.form.get('enabled').valueChanges
      .subscribe(() => this.syncEnabledControls());
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.frequencySubscription?.unsubscribe();
    this.monthlyModeSubscription?.unsubscribe();
    this.endsModeSubscription?.unsubscribe();
    this.enabledSubscription?.unsubscribe();
  }

  get enabled(): boolean {
    return this.form?.get('enabled')?.value ?? false;
  }

  get frequency(): RecurrenceFrequency {
    return this.form?.get('frequency')?.value ?? 'Weekly';
  }

  get monthlyMode(): 'ByDayOfMonth' | 'ByNthWeekday' {
    return this.form?.get('monthlyMode')?.value ?? 'ByDayOfMonth';
  }

  get endsMode(): 'Count' | 'Date' {
    return this.form?.get('endsMode')?.value ?? 'Count';
  }

  /** Set when the chosen end date is before the appointment itself. */
  get endsBeforeStart(): boolean {
    return !!this.form?.errors?.['endsOnDateBeforeStart'];
  }

  /** Set when the chosen end date is past the server's horizon from the first occurrence. */
  get endsBeyondHorizon(): boolean {
    return !!this.form?.errors?.['endsOnDateBeyondHorizon'];
  }

  /** The largest "every N" the chosen frequency allows — 365 days, 52 weeks, 12 months. */
  get maxInterval(): number {
    return maxIntervalFor(this.frequency);
  }

  /** "days" / "weeks" / "months", for the limit hint beside the interval. */
  get intervalUnit(): string {
    return RECURRENCE_INTERVAL_UNIT[this.frequency] ?? 'units';
  }

  /** Set when "every N" is longer than the chosen frequency can repeat inside the horizon. */
  get intervalAboveLimit(): boolean {
    return !!this.form?.errors?.['intervalAboveFrequencyLimit'];
  }

  /** Set when a count-ended repeat would reach past the server's horizon. */
  get countBeyondHorizon(): boolean {
    return !!this.form?.errors?.['countBeyondHorizon'];
  }

  /** How many appointments the current pattern actually fits inside the horizon. */
  get occurrencesThatFit(): number {
    return this.form?.errors?.['countBeyondHorizon']?.fits ?? 0;
  }

  /** Set when a date-ended repeat would make more appointments than the server allows. */
  get dateMakesTooMany(): boolean {
    return !!this.form?.errors?.['dateBeyondOccurrenceCap'];
  }

  /** The latest end date the server will accept for the current start. */
  get latestEndDate(): Date | null {
    const start = this.startDate();
    return start ? dayjs(start).startOf('day').add(RECURRENCE_HORIZON_DAYS, 'day').toDate() : null;
  }

  /** Weekly days are checkboxes over one array control — toggle membership by hand. */
  toggleWeekday(day: RecurrenceWeekday, checked: boolean): void {
    const control = this.form.get('weeklyDays');
    const current = (control.value ?? []) as RecurrenceWeekday[];
    const next = checked
      ? [...current, day]
      : current.filter(d => d !== day);
    // Keep calendar order regardless of click order, so the summary reads naturally.
    control.setValue(this.weekdays.filter(d => next.includes(d)));
    // setValue leaves the control pristine, so mark it by hand — pristine is what
    // reseedFromStart uses to decide whether the user owns this value yet.
    control.markAsDirty();
  }

  isWeekdaySelected(day: RecurrenceWeekday): boolean {
    return ((this.form?.get('weeklyDays')?.value ?? []) as RecurrenceWeekday[]).includes(day);
  }

  /**
   * Re-point the seeded defaults at a new appointment start.
   *
   * Only controls the user has not touched are moved. Pristine is the same signal the
   * new-patient form uses to decide whether it may default a title, so the rule is already
   * familiar in this codebase: once you type in a field, it is yours.
   */
  private reseedFromStart(start: Date): void {
    const next = dayjs(start);
    if (!next.isValid()) {
      return;
    }
    this.setIfPristine('weeklyDays', [this.weekdays[next.day()]]);
    this.setIfPristine('monthlyDayOfMonth', next.date());
    this.setIfPristine('monthlyWeekday', this.weekdays[next.day()]);
    this.setIfPristine('endsOnDate', next.add(1, 'month').toDate());
    // The horizon is measured from the start, so a start move can validate or invalidate
    // an end date the user did choose.
    this.form.updateValueAndValidity({emitEvent: false});
    this.emit();
  }

  private setIfPristine(name: string, value: unknown): void {
    const control = this.form.get(name);
    if (control.pristine) {
      control.setValue(value, {emitEvent: false});
    }
  }

  /**
   * The end date must land on or after the appointment, and within the server's horizon of
   * it. Checked at group level because it needs both the start input and the end control.
   * Only meaningful while the Date branch is the live one.
   */
  private endDateWithinHorizon(control: AbstractControl): ValidationErrors | null {
    if (control.get('endsMode')?.value !== 'Date') return null;

    const start = this.startDate();
    const endsOnDate = control.get('endsOnDate')?.value;
    if (!start || !endsOnDate) return null;

    const startDay = dayjs(start).startOf('day');
    const endDay = dayjs(endsOnDate).startOf('day');
    if (!startDay.isValid() || !endDay.isValid()) return null;

    if (endDay.isBefore(startDay)) {
      return {endsOnDateBeforeStart: true};
    }
    if (endDay.isAfter(startDay.add(RECURRENCE_HORIZON_DAYS, 'day'))) {
      return {endsOnDateBeyondHorizon: true};
    }
    return null;
  }

  /**
   * "Every N" is bounded by what the chosen frequency can actually repeat within the horizon:
   * 365 days, 52 weeks, 12 months.
   *
   * A group validator rather than `Validators.max` on the control, because the limit moves
   * with the frequency — switching monthly to daily has to widen it, and switching back has
   * to narrow it, without rebuilding the control.
   *
   * Reported on the group rather than set on the control so the frequency change alone
   * re-evaluates it; the interval control's own value has not changed at that point.
   */
  private intervalWithinFrequencyLimit(control: AbstractControl): ValidationErrors | null {
    const interval = control.get('interval')?.value;
    const frequency = control.get('frequency')?.value as RecurrenceFrequency;

    // Presence, sign and whole-ness are the control's own validators to report.
    if (!interval || interval < 1 || !Number.isInteger(interval) || !frequency) {
      return null;
    }

    const limit = maxIntervalFor(frequency);
    return interval > limit ? {intervalAboveFrequencyLimit: {limit, unit: RECURRENCE_INTERVAL_UNIT[frequency]}} : null;
  }

  /**
   * A count-ended repeat has to fit inside the horizon too.
   *
   * `endsAfterCount` bounds how many appointments a repeat makes, not how far it reaches:
   * weekly x interval 1 x 60 occurrences is 420 days, well past the 365-day limit. The
   * server expands the pattern, stops at the horizon, and rejects the series for producing
   * fewer occurrences than were asked for. Projecting the same walk here turns that into a
   * message on the field instead of a save that fails.
   *
   * Only the Date branch was bounded before this; see `endDateWithinHorizon`.
   */
  private countWithinHorizon(control: AbstractControl): ValidationErrors | null {
    if (control.get('endsMode')?.value !== 'Count') return null;

    const count = control.get('endsAfterCount')?.value;
    if (!count || count < 1 || !Number.isInteger(count)) return null;

    const startDay = this.projectableStart(control);
    if (!startDay) return null;

    const horizon = startDay.add(RECURRENCE_HORIZON_DAYS, 'day');
    const fits = this.occurrencesUpTo(control, startDay, horizon, count);
    return fits >= count ? null : {countBeyondHorizon: {fits}};
  }

  /**
   * A date-ended repeat has to fit inside the occurrence cap too.
   *
   * The mirror of `countWithinHorizon`: that one bounds a count by the horizon, this one
   * bounds a date by the count. Daily at interval 1 from the appointment to the latest date
   * the picker offers is 366 appointments — one past the cap — so the form stayed valid and
   * the server rejected the series for having too many, while the hint under the field
   * promised at most 365.
   */
  private dateWithinOccurrenceCap(control: AbstractControl): ValidationErrors | null {
    if (control.get('endsMode')?.value !== 'Date') return null;

    const endsOnDate = control.get('endsOnDate')?.value;
    if (!endsOnDate) return null;

    const startDay = this.projectableStart(control);
    if (!startDay) return null;

    const endDay = dayjs(endsOnDate).startOf('day');
    if (!endDay.isValid()) return null;
    // An end date outside the horizon is `endDateWithinHorizon`'s to report; projecting one
    // would only add a second error to a field that already has one.
    if (endDay.isBefore(startDay) || endDay.isAfter(startDay.add(RECURRENCE_HORIZON_DAYS, 'day'))) {
      return null;
    }

    // One past the cap is all that needs counting: the walk stops there, and the only
    // question is whether the pattern gets that far.
    const produced = this.occurrencesUpTo(control, startDay, endDay, RECURRENCE_MAX_OCCURRENCES + 1);
    return produced > RECURRENCE_MAX_OCCURRENCES ? {dateBeyondOccurrenceCap: true} : null;
  }

  /**
   * The appointment's start day, or null when the pattern is not complete enough to project.
   *
   * Each control checked here carries its own validator, so a second error from a projection
   * would only be noise. The shape of the interval matters as much as its range: a
   * non-positive one would never advance the walk, and a fractional one would step fractional
   * days and count a series the server could not produce.
   */
  private projectableStart(control: AbstractControl): Dayjs | null {
    const start = this.startDate();
    if (!start) return null;

    const interval = control.get('interval')?.value;
    if (!interval || interval < 1 || !Number.isInteger(interval)) return null;

    // An interval past its frequency's limit is already reported as an interval. Projecting it
    // anyway would add "only fits 1 appointment" on top — the very message this replaced,
    // describing the symptom rather than what the user chose.
    if (interval > maxIntervalFor(control.get('frequency')?.value as RecurrenceFrequency)) return null;

    if (control.get('frequency')?.value === 'Monthly') {
      const pattern = control.get('monthlyMode')?.value === 'ByNthWeekday'
        ? control.get('monthlyNth')?.value && control.get('monthlyWeekday')?.value
        : control.get('monthlyDayOfMonth')?.value;
      if (!pattern) return null;
    }

    const startDay = dayjs(start).startOf('day');
    return startDay.isValid() ? startDay : null;
  }

  /**
   * How many occurrences the pattern produces between the appointment and `until` inclusive,
   * counting no further than `limit`.
   *
   * Both end conditions project through here — Count bounds by the horizon and asks whether
   * the pattern reaches its count, Date bounds by the chosen end date and asks whether it
   * produces too many. Same walk either way, which is what keeps the two answers consistent.
   *
   * Mirrors the server's expander anchor-for-anchor and skip-for-skip. A projection that
   * disagrees with it is worse than none: too strict and it blocks a series the server
   * would have taken, too loose and it promises one the server will reject. Candidates are
   * walked in ascending order and the walk stops at the first one past `until`, so the cost
   * is bounded by the date range rather than by `limit`.
   */
  private occurrencesUpTo(control: AbstractControl, startDay: Dayjs, until: Dayjs, limit: number): number {
    const interval = control.get('interval').value as number;
    const frequency = control.get('frequency').value as RecurrenceFrequency;

    let counted = 0;
    /** Counts one candidate. Returns false once `limit` is reached, to stop the walk. */
    const take = (candidate: Dayjs): boolean => {
      // Candidates can precede the appointment — the weekly walk anchors on the containing
      // week — and those are skipped rather than counted, exactly as the server does.
      if (candidate.isBefore(startDay)) return true;
      counted++;
      return counted < limit;
    };

    if (frequency === 'Daily') {
      for (let date = startDay; !date.isAfter(until); date = date.add(interval, 'day')) {
        if (!take(date)) break;
      }
      return counted;
    }

    if (frequency === 'Weekly') {
      const selected = (control.get('weeklyDays').value ?? []) as RecurrenceWeekday[];
      // No days chosen means "the same weekday the appointment starts on" — the natural
      // reading of "repeat weekly", and the server's too.
      const dayIndexes = selected.length > 0
        ? [...new Set(selected.map(day => this.weekdays.indexOf(day)))].sort((a, b) => a - b)
        : [startDay.day()];
      // Anchor on the Sunday of the appointment's own week, so "every N weeks" counts whole
      // weeks from a fixed point rather than drifting with whichever day was picked first.
      const weekAnchor = startDay.subtract(startDay.day(), 'day');

      for (let block = weekAnchor; !block.isAfter(until); block = block.add(7 * interval, 'day')) {
        let stop = false;
        for (const index of dayIndexes) {
          const candidate = block.add(index, 'day');
          if (candidate.isAfter(until)) continue;
          if (!take(candidate)) {
            stop = true;
            break;
          }
        }
        if (stop) break;
      }
      return counted;
    }

    const monthlyMode = control.get('monthlyMode').value as 'ByDayOfMonth' | 'ByNthWeekday';
    for (let month = startDay.startOf('month'); !month.isAfter(until); month = month.add(interval, 'month')) {
      const candidate = monthlyMode === 'ByNthWeekday'
        ? this.nthWeekdayOf(month, control.get('monthlyNth').value, control.get('monthlyWeekday').value)
        : this.dayOfMonthIn(month, control.get('monthlyDayOfMonth').value);

      // A month with no such day — the 31st of February — is skipped whole rather than
      // clamped back to the 28th, matching the server. Clamping would silently move an
      // appointment to a day nobody picked.
      if (!candidate) continue;
      // Only the month anchor is bounded above, so the day itself can still land past the
      // bound. On the horizon the server would emit that occurrence and then reject the
      // series for finishing too late; on an end date it stops before it. Either way it is
      // not part of the series, so stop here.
      if (candidate.isAfter(until)) break;
      if (!take(candidate)) break;
    }
    return counted;
  }

  /** That day of the given month, or null when the month has no such day. */
  private dayOfMonthIn(month: Dayjs, dayOfMonth: number): Dayjs | null {
    return dayOfMonth <= month.daysInMonth() ? month.date(dayOfMonth) : null;
  }

  /**
   * The First..Fourth or Last given weekday of the month. First..Fourth always resolve —
   * every month holds at least four of every weekday, which is why Fifth is not offered.
   */
  private nthWeekdayOf(month: Dayjs, nth: RecurrenceNthWeek, weekday: RecurrenceWeekday): Dayjs {
    const target = this.weekdays.indexOf(weekday);
    if (nth === 'Last') {
      const lastDay = month.endOf('month').startOf('day');
      return lastDay.subtract((lastDay.day() - target + 7) % 7, 'day');
    }
    const firstMatching = month.add((target - month.day() + 7) % 7, 'day');
    return firstMatching.add(7 * this.nthWeeks.indexOf(nth), 'day');
  }

  /**
   * Disable every control that the current frequency / mode doesn't use. Disabled controls
   * are excluded from `form.valid`, which is what stops (say) a stale monthly day-of-month
   * from blocking save on a weekly repeat.
   */
  private syncEnabledControls(): void {
    const on = this.enabled;
    const weekly = on && this.frequency === 'Weekly';
    const monthly = on && this.frequency === 'Monthly';

    this.setEnabled('frequency', on);
    this.setEnabled('interval', on);
    this.setEnabled('weeklyDays', weekly);
    this.setEnabled('monthlyMode', monthly);
    this.setEnabled('monthlyDayOfMonth', monthly && this.monthlyMode === 'ByDayOfMonth');
    this.setEnabled('monthlyNth', monthly && this.monthlyMode === 'ByNthWeekday');
    this.setEnabled('monthlyWeekday', monthly && this.monthlyMode === 'ByNthWeekday');
    this.setEnabled('endsMode', on);
    this.setEnabled('endsAfterCount', on && this.endsMode === 'Count');
    this.setEnabled('endsOnDate', on && this.endsMode === 'Date');

    this.emit();
  }

  private setEnabled(name: string, enabled: boolean): void {
    const control = this.form.get(name);
    if (enabled === control.enabled) {
      return;
    }
    if (enabled) {
      control.enable({emitEvent: false});
    } else {
      control.disable({emitEvent: false});
    }
  }

  private emit(): void {
    this.recurrenceChange.emit(this.buildRequest());
    this.validityChange.emit(!this.enabled || this.form.valid);
  }

  /** The rule to send, or null when repeating is off or the form isn't yet valid. */
  private buildRequest(): RecurrenceRequest | null {
    if (!this.enabled || !this.form.valid) {
      return null;
    }
    // getRawValue would include the disabled branches; .value gives only the live ones,
    // which is exactly the mutual exclusivity the server requires.
    const value = this.form.value;

    const request: RecurrenceRequest = {
      frequency: value.frequency,
      interval: value.interval,
    };

    if (value.frequency === 'Weekly') {
      request.weeklyDays = value.weeklyDays ?? [];
    }
    if (value.frequency === 'Monthly') {
      if (value.monthlyMode === 'ByDayOfMonth') {
        request.monthlyDayOfMonth = value.monthlyDayOfMonth;
      } else {
        request.monthlyNth = value.monthlyNth;
        request.monthlyWeekday = value.monthlyWeekday;
      }
    }
    if (value.endsMode === 'Count') {
      request.endsAfterCount = value.endsAfterCount;
    } else {
      // Send the bare wall-clock the user picked, never the Date itself. The picker holds
      // a device-local midnight, and JSON.stringify would emit it as a UTC instant — from
      // SAST, choosing 30 June sends 2026-06-29T22:00Z, which the server reads as the
      // 29th and drops the final occurrence. Same rule the appointment start/end follow.
      request.endsOnDate = pickerToWallClock(value.endsOnDate);
    }
    return request;
  }
}
